import { useCallback, useEffect, useState } from 'react';
import type { Venue, VenueType } from '@vigilo/shared';
import { get, post, patch } from '@vigilo/api-client';
import { useMockMode } from '@/shell/MockModeContext';
import { MOCK_OPERATIONS, MOCK_VENUES, MOCK_ZONES } from '../data/mock';
import { crowdQuery } from '../apiScope';

export const CROWD_VENUES_CHANGED_EVENT = 'vigilo:crowd:venues-changed';

export interface VenueWithCounts extends Venue {
  activeZoneCount:      number;
  activeOperationCount: number;
}

export interface CreateVenueDto {
  name:          string;
  venueType:     VenueType;
  city:          string;
  country:       string;
  address?:      string;
  totalCapacity: number;
}

export interface UpdateVenueDto {
  name?:          string;
  venueType?:     VenueType;
  city?:          string;
  country?:       string;
  address?:       string | null;
  totalCapacity?: number;
}

export interface VenuesHookState {
  venues:       VenueWithCounts[];
  loading:      boolean;
  error:        string | null;
  createVenue:  (dto: CreateVenueDto) => Promise<VenueWithCounts>;
  updateVenue:  (id: string, dto: UpdateVenueDto) => Promise<void>;
  archiveVenue: (id: string) => Promise<void>;
  refresh:      () => Promise<void>;
}

function unwrap<T>(body: T | { data?: T }): T {
  const b = body as { data?: T };
  return (b.data !== undefined ? b.data : body) as T;
}

function unwrapList<T>(body: T[] | { data?: T[] }): T[] {
  return Array.isArray(body) ? body : (body.data ?? []);
}

function withVenueCounts(venues: Venue[]): VenueWithCounts[] {
  return venues.map(venue => ({
    ...venue,
    activeZoneCount: MOCK_ZONES.filter(zone => zone.venueId === venue.id && !zone.archivedAt).length,
    activeOperationCount: MOCK_OPERATIONS.filter(
      operation => operation.venueId === venue.id && (operation.status === 'active' || operation.status === 'paused'),
    ).length,
  }));
}

function upsertNewest(list: VenueWithCounts[], venue: VenueWithCounts): VenueWithCounts[] {
  return [venue, ...list.filter(v => v.id !== venue.id)];
}

let mockVenuesStore: VenueWithCounts[] | null = null;

function currentMockVenues(): VenueWithCounts[] {
  if (!mockVenuesStore) {
    mockVenuesStore = withVenueCounts(MOCK_VENUES);
  }
  return mockVenuesStore;
}

function notifyVenuesChanged(): void {
  window.dispatchEvent(new Event(CROWD_VENUES_CHANGED_EVENT));
}

export function useVenues(): VenuesHookState {
  const { isMock } = useMockMode();
  const [venues,  setVenues]  = useState<VenueWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isMock) {
        await new Promise(r => setTimeout(r, 80));
        setVenues(currentMockVenues());
      } else {
        const body = await get<VenueWithCounts[] | { data?: VenueWithCounts[] }>(`/venues?${crowdQuery({ archiveStatus: 'all' })}`);
        setVenues(unwrapList(body));
      }
    } catch {
      setError('Failed to load venues — check Core API');
    } finally {
      setLoading(false);
    }
  }, [isMock]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    function handleVenuesChanged() {
      void refresh();
    }
    window.addEventListener(CROWD_VENUES_CHANGED_EVENT, handleVenuesChanged);
    return () => window.removeEventListener(CROWD_VENUES_CHANGED_EVENT, handleVenuesChanged);
  }, [refresh]);

  const createVenue = useCallback(async (dto: CreateVenueDto): Promise<VenueWithCounts> => {
    if (isMock) {
      const now = new Date().toISOString();
      const created: VenueWithCounts = {
        id:            crypto.randomUUID(),
        name:          dto.name,
        venueType:     dto.venueType,
        city:          dto.city,
        country:       dto.country,
        address:       dto.address ?? null,
        totalCapacity: dto.totalCapacity,
        geometry:      null,
        createdBy:     'mock-user-001',
        archivedAt:    null,
        createdAt:     now,
        updatedAt:     now,
        activeZoneCount:      0,
        activeOperationCount: 0,
      };
      mockVenuesStore = upsertNewest(currentMockVenues(), created);
      setVenues(mockVenuesStore);
      notifyVenuesChanged();
      return created;
    }
    const body = await post<VenueWithCounts | { data?: VenueWithCounts }>('/venues', dto);
    const venue = unwrap(body);
    setVenues(prev => upsertNewest(prev, venue));

    try {
      const refreshed = await get<VenueWithCounts[] | { data?: VenueWithCounts[] }>(`/venues?${crowdQuery({ archiveStatus: 'all' })}`);
      const list = unwrapList(refreshed);
      setVenues(list.some(v => v.id === venue.id) ? list : upsertNewest(list, venue));
    } catch {
      // Keep the created venue visible even if the post-create refresh fails.
    }
    notifyVenuesChanged();

    return venue;
  }, [isMock]);

  const updateVenue = useCallback(async (id: string, dto: UpdateVenueDto): Promise<void> => {
    if (isMock) {
      mockVenuesStore = currentMockVenues().map(v => v.id === id ? { ...v, ...dto, updatedAt: new Date().toISOString() } : v);
      setVenues(mockVenuesStore);
      notifyVenuesChanged();
      return;
    }
    await patch<unknown>(`/venues/${id}`, dto);
    await refresh();
    notifyVenuesChanged();
  }, [isMock, refresh]);

  const archiveVenue = useCallback(async (id: string): Promise<void> => {
    if (isMock) {
      mockVenuesStore = currentMockVenues().map(v => v.id === id ? { ...v, archivedAt: new Date().toISOString() } : v);
      setVenues(mockVenuesStore);
      notifyVenuesChanged();
      return;
    }
    await post<unknown>(`/venues/${id}/archive`, {});
    await refresh();
    notifyVenuesChanged();
  }, [isMock, refresh]);

  return { venues, loading, error, createVenue, updateVenue, archiveVenue, refresh };
}
